/**
 * server-monitor-adapter — 服务器监控真 DTO（snake_case，`@novel-admin/shared` server-monitor）→ 页面行（camelCase + 展示文案）。
 *
 * 时间按浏览器本地时区展示：今天 `HH:mm:ss`、昨天 `昨天 HH:mm`、更早 `MM-DD HH:mm`；日志行到毫秒。
 * 契约缺的值落「—」，不编造。支付到账时效告警（alert.latency 非空）没有 HTTP 路由：行内展示 provider + 指标。
 * 接口时效告警（alert.api_latency 非空）展示 trigger + 分位数；「接口时效」卡的行由 overview.api_latency 塑形。
 */

import {
  SERVER_MONITOR_FEISHU_GROUP_NAMES,
  SERVER_MONITOR_LOG_RANGE_MS,
  SERVER_MONITOR_RATE_LIMIT_DEGRADED_EVENT,
  SERVER_MONITOR_RATE_LIMIT_DEGRADED_QUERY_FAILED_EVENT,
  type ServerMonitorAlert,
  type ServerMonitorAlertLatency,
  type ServerMonitorApiLatency,
  type ServerMonitorApiLatencyItem,
  type ServerMonitorApiLatencyQuery,
  type ServerMonitorCategory,
  type ServerMonitorHealthCategory,
  type ServerMonitorLogItem,
  type ServerMonitorLogRange,
  type ServerMonitorOverview,
  type ServerMonitorPaymentLatency,
  type ServerMonitorPaymentMetric,
  type ServerMonitorPaymentProvider,
  type ServerMonitorService,
  serverMonitorOngoingBackoffExempt,
  serverMonitorOngoingRenotifyMinutes,
} from '@novel-admin/shared';
import type { Icons } from '~/lib/icons';

/** 环境切换按钮文案 */
const SERVER_MONITOR_SERVICE_LABELS: Record<ServerMonitorService, string> = {
  'novel-app-server-production': '生产',
  'novel-app-server-dev': '开发',
};

/** 环境切换选项：只给本环境可查看的 service（overview.viewable_services，顺序照后端）*/
export function toServiceOptions(
  viewable: readonly ServerMonitorService[],
): Array<{ service: ServerMonitorService; label: string }> {
  return viewable.map((service) => ({ service, label: SERVER_MONITOR_SERVICE_LABELS[service] }));
}

export const SERVER_MONITOR_CATEGORY_META: Record<
  ServerMonitorCategory,
  { label: string; icon: keyof typeof Icons }
> = {
  auth: { label: '注册登录', icon: 'IconUsers' },
  reading: { label: '阅读', icon: 'IconBook' },
  payment: { label: '支付', icon: 'IconWallet' },
  platform: { label: '平台', icon: 'IconMonitor' },
  other: { label: '其他', icon: 'IconLayers' },
};

/** 评估停滞判据：cron 每 2 分钟一轮，超过 6 分钟没有新 tick 视为停滞 */
const EVAL_STALE_MS = 6 * 60_000;

function pad(n: number, w = 2): string {
  return String(n).padStart(w, '0');
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** 相对展示时间：今天 HH:mm:ss / 昨天 HH:mm / MM-DD HH:mm；空值「—」*/
export function fmtMonitorTime(iso: string | null | undefined, nowMs: number): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date(nowMs);
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (sameLocalDay(d, now)) return `${hm}:${pad(d.getSeconds())}`;
  const yesterday = new Date(nowMs - 24 * 60 * 60_000);
  if (sameLocalDay(d, yesterday)) return `昨天 ${hm}`;
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${hm}`;
}

/** 日志行时间：今天 HH:mm:ss.SSS / 否则 MM-DD HH:mm:ss */
export function fmtLogTime(iso: string, nowMs: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hms = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  if (sameLocalDay(d, new Date(nowMs))) return `${hms}.${pad(d.getMilliseconds(), 3)}`;
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${hms}`;
}

function routeOf(method: string | null, path: string | null): string {
  return [method, path].filter(Boolean).join(' ') || '—';
}

// ---------- 支付到账时效 ----------

export const SERVER_MONITOR_PAYMENT_PROVIDER_LABELS: Record<ServerMonitorPaymentProvider, string> =
  {
    adapty: 'Adapty',
    stripe: 'Stripe 原生',
    web_stripe: 'Web Stripe',
  };

const PAYMENT_METRIC_LABELS: Record<ServerMonitorPaymentMetric, string> = {
  processing: '到账处理耗时 p95',
  backlog: '到账积压',
  delivery: '回调送达延迟 p95',
  callback_missing: '校验后到账缺失',
};

/** 毫秒 → `850ms` / `34.2s` / `2m5s`（与飞书消息同口径）*/
export function fmtLatencyMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 100_000) return `${(ms / 1000).toFixed(1)}s`;
  const totalSeconds = Math.round(ms / 1000);
  return `${Math.floor(totalSeconds / 60)}m${totalSeconds % 60}s`;
}

function latencyValue(latency: ServerMonitorAlertLatency, value: number): string {
  return latency.unit === 'ms' ? fmtLatencyMs(value) : `${value} 笔`;
}

// ---------- 健康卡 ----------

export interface HealthCardRow {
  category: ServerMonitorCategory;
  label: string;
  icon: keyof typeof Icons;
  state: ServerMonitorHealthCategory['status'];
  stateLabel: string;
  lastHourCount: number;
  last24hCount: number;
  lastLabel: string;
  buckets: number[];
}

const HEALTH_STATE_LABEL: Record<ServerMonitorHealthCategory['status'], string> = {
  ok: '正常',
  warn: '有异常',
  firing: '告警中',
};

export function toHealthCardRows(overview: ServerMonitorOverview, nowMs: number): HealthCardRow[] {
  return overview.health.categories.map((c) => ({
    category: c.category,
    label: SERVER_MONITOR_CATEGORY_META[c.category].label,
    icon: SERVER_MONITOR_CATEGORY_META[c.category].icon,
    state: c.status,
    stateLabel: HEALTH_STATE_LABEL[c.status],
    lastHourCount: c.last_hour_count,
    last24hCount: c.last_24h_count,
    lastLabel: fmtMonitorTime(c.last_occurred_at, nowMs),
    buckets: c.buckets,
  }));
}

// ---------- 告警 ----------

export interface AlertRow {
  fingerprint: string;
  severity: 'P0' | 'P1';
  category: ServerMonitorCategory;
  categoryLabel: string;
  recovered: boolean;
  statusLabel: string;
  /** chip tone：新告警 neg / 持续中 warn / 已恢复 pos */
  statusTone: 'neg' | 'warn' | 'pos';
  route: string;
  meta: string;
  countLabel: string;
  firstLabel: string;
  lastLabel: string;
  /** 活跃：飞书推送行；已恢复：恢复行 */
  noticeLabel: string;
  lastSeenAt: string;
  /** 支付到账时效 / 接口时效告警（无错误日志可下钻，不显示「日志」按钮）*/
  latency: boolean;
}

/** 接口时效告警的路由列：接口直接用 trigger，队列加「队列」前缀 */
function apiLatencyRoute(api: NonNullable<ServerMonitorAlert['api_latency']>): string {
  return api.kind === 'queue' ? `队列 · ${api.trigger}` : api.trigger;
}

function apiLatencyMeta(api: NonNullable<ServerMonitorAlert['api_latency']>): string {
  return [
    `p95 ${fmtLatencyMs(api.p95_ms)} > 阈值 ${fmtLatencyMs(api.threshold_ms)}`,
    `p50 ${fmtLatencyMs(api.p50_ms)} · p99 ${fmtLatencyMs(api.p99_ms)}`,
    `样本 ${api.samples} ${api.kind === 'queue' ? '批' : '次'}`,
    `窗口 ${api.window_minutes} 分钟`,
  ].join(' · ');
}

/** 同一主体降级放行突增：只展示达标主体数与最高次数及判据，不展示计数主体 */
function rateLimitDegradedMeta(
  stats: NonNullable<ServerMonitorAlert['rate_limit_degraded']>,
): string {
  return [
    `达标主体 ${stats.flagged_subjects}${stats.flagged_subjects_capped ? '+' : ''} 个`,
    `最高 ${stats.max_count} 次`,
    `判据 单主体 ≥ ${stats.threshold} 次 / ${stats.window_minutes} 分钟`,
    stats.sampled ? '计数为估算值' : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const ALERT_STATUS: Record<
  ServerMonitorAlert['status'],
  { label: string; tone: AlertRow['statusTone'] }
> = {
  new: { label: '新告警', tone: 'neg' },
  ongoing: { label: '持续中', tone: 'warn' },
  recovered: { label: '已恢复', tone: 'pos' },
};

/** 与 cron 同口径的下一次「持续中」重推间隔：付了钱没到账类支付 P0 不退避，其余按送达次数翻倍 */
function renotifyMinutes(alert: ServerMonitorAlert, cooldownMinutes: number): number {
  const exempt = serverMonitorOngoingBackoffExempt({
    category: alert.category,
    severity: alert.severity,
    paymentMetric: alert.latency?.metric ?? null,
    apiLatency: alert.api_latency !== null,
  });
  return exempt
    ? cooldownMinutes
    : serverMonitorOngoingRenotifyMinutes(cooldownMinutes, alert.notification_count);
}

export function toAlertRow(
  alert: ServerMonitorAlert,
  ctx: { nowMs: number; windowMinutes: number; cooldownMinutes: number },
): AlertRow {
  const recovered = alert.status === 'recovered';
  const latency = alert.latency;
  const apiLatency = alert.api_latency;
  // 积压 / 回调缺失的 window_minutes 是回看时长（与 p95 统计窗口无关）
  const rateLimitDegraded = alert.rate_limit_degraded;
  const meta = apiLatency
    ? apiLatencyMeta(apiLatency)
    : rateLimitDegraded
      ? rateLimitDegradedMeta(rateLimitDegraded)
      : latency
        ? (latency.metric === 'backlog' || latency.metric === 'callback_missing'
            ? [
                `${latency.metric === 'backlog' ? '积压' : '缺失'} ${latencyValue(latency, latency.value)} > 阈值 ${latencyValue(latency, latency.threshold)}`,
                `回看 ${Math.round(latency.window_minutes / 60)}h`,
              ]
            : [
                `${latencyValue(latency, latency.value)} > 阈值 ${latencyValue(latency, latency.threshold)}`,
                `样本 ${latency.samples} 笔`,
                `窗口 ${latency.window_minutes} 分钟`,
              ]
          ).join(' · ')
        : [alert.event, alert.error_code ?? '—', alert.http_status ?? null]
            .filter((v) => v !== null)
            .join(' · ');
  // 短暂故障的恢复 / 关闭不单独推飞书（recovery_notified = false），标签如实说明；
  // 字段缺失（旧版 server 响应）按已推送，与服务端旧数据口径一致
  const recoveryNotice = alert.recovery_notified !== false ? '已发飞书' : '未单独推送';
  const noticeLabel = alert.closed_no_traffic
    ? `无流量关闭 · ${fmtMonitorTime(alert.recovered_at, ctx.nowMs)} · 未确认恢复 · ${recoveryNotice}关闭通知`
    : recovered
      ? `恢复 · ${fmtMonitorTime(alert.recovered_at, ctx.nowMs)} · ${recoveryNotice}恢复通知`
      : alert.last_notified_at
        ? `飞书 · ${fmtMonitorTime(alert.last_notified_at, ctx.nowMs)} · 第 ${alert.notification_count} 次 · 冷却 ${renotifyMinutes(alert, ctx.cooldownMinutes)} 分钟`
        : '飞书 · 未送达（下一轮重发）';
  return {
    fingerprint: alert.fingerprint,
    severity: alert.severity,
    category: alert.category,
    categoryLabel: SERVER_MONITOR_CATEGORY_META[alert.category].label,
    recovered,
    // 无流量关闭不是确认恢复：标签与色调和「已恢复」区分开
    statusLabel: alert.closed_no_traffic ? '无流量关闭' : ALERT_STATUS[alert.status].label,
    statusTone: alert.closed_no_traffic ? 'warn' : ALERT_STATUS[alert.status].tone,
    route: apiLatency
      ? apiLatencyRoute(apiLatency)
      : latency
        ? `${SERVER_MONITOR_PAYMENT_PROVIDER_LABELS[latency.provider]} · ${PAYMENT_METRIC_LABELS[latency.metric]}`
        : routeOf(alert.method, alert.path),
    meta,
    countLabel: apiLatency
      ? `累计超阈值 ${alert.total_count} 轮`
      : latency
        ? [
            `累计超阈值 ${alert.total_count} 轮`,
            latency.slowest_event_id
              ? latency.metric === 'callback_missing'
                ? `最老 trigger ${latency.slowest_event_id} · 校验完成 ${fmtMonitorTime(latency.slowest_received_at, ctx.nowMs)}`
                : `${latency.metric === 'backlog' ? '最老' : '最慢'} ${latency.slowest_event_id} · 到达 ${fmtMonitorTime(latency.slowest_received_at, ctx.nowMs)}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : alert.event === SERVER_MONITOR_RATE_LIMIT_DEGRADED_EVENT ||
            alert.event === SERVER_MONITOR_RATE_LIMIT_DEGRADED_QUERY_FAILED_EVENT
          ? `累计 ${alert.total_count} 轮`
          : alert.window_count === null
            ? `累计 ${alert.total_count} 次`
            : `${alert.window_count} 次 / ${ctx.windowMinutes} 分钟`,
    firstLabel: fmtMonitorTime(alert.first_seen_at, ctx.nowMs),
    lastLabel: fmtMonitorTime(alert.last_seen_at, ctx.nowMs),
    noticeLabel,
    lastSeenAt: alert.last_seen_at,
    latency: latency !== null || apiLatency !== null,
  };
}

/** 告警「日志」按钮：保证时间范围覆盖该指纹的最近一次出现 */
export function rangeCoveringAlert(
  lastSeenAt: string,
  current: ServerMonitorLogRange,
  nowMs: number,
): ServerMonitorLogRange {
  const age = nowMs - Date.parse(lastSeenAt);
  if (!(age > SERVER_MONITOR_LOG_RANGE_MS[current])) return current;
  const order: ServerMonitorLogRange[] = ['15m', '1h', '24h', '7d'];
  return order.find((r) => SERVER_MONITOR_LOG_RANGE_MS[r] >= age) ?? '7d';
}

// ---------- 规则 ----------

export interface RuleRow {
  severity: 'P0' | 'P1';
  name: string;
  condition: string;
  note: string;
}

export function toRuleRows(overview: ServerMonitorOverview): RuleRow[] {
  const window = overview.rules.thresholds.window_minutes;
  const logRules = overview.rules.items.map((rule) => ({
    severity: rule.severity,
    name: rule.name,
    condition:
      rule.scope === 'fingerprint'
        ? `同指纹 · ${window} 分钟内 ≥ ${rule.threshold} 次`
        : `${rule.categories.map((c) => SERVER_MONITOR_CATEGORY_META[c].label).join(' / ')} · ${window} 分钟内 ≥ ${rule.threshold} 次`,
    note: rule.note,
  }));
  const pt = overview.rules.payment_latency.thresholds;
  const paymentRules = overview.rules.payment_latency.items.map((rule) => {
    const providers = rule.providers
      .map((p) => SERVER_MONITOR_PAYMENT_PROVIDER_LABELS[p])
      .join(' / ');
    const condition =
      rule.metric === 'processing'
        ? `${providers} · 近 ${window} 分钟处理耗时 p95 > ${pt.processing_p95_seconds}s（样本 ≥ ${pt.min_samples}）`
        : rule.metric === 'backlog'
          ? `${providers} · 回看 ${pt.backlog_lookback_hours}h 到达超 ${pt.backlog_age_seconds}s 仍未生效 > ${pt.backlog_count} 笔`
          : rule.metric === 'callback_missing'
            ? `${providers} · 回看 ${pt.backlog_lookback_hours}h 校验成功后同一用户无 Stripe 到账 > 0 笔`
            : `${providers} · 近 ${window} 分钟回调送达 p95 > ${pt.delivery_p95_seconds}s（样本 ≥ ${pt.min_samples}）`;
    return { severity: rule.severity, name: rule.name, condition, note: rule.note };
  });
  const at = overview.rules.api_latency.thresholds;
  const apiRules = overview.rules.api_latency.items.map((rule) => ({
    severity: rule.severity,
    name: rule.name,
    condition:
      rule.scope === 'core'
        ? `注册登录 / 阅读 / 支付接口 · 近 ${window} 分钟 p95 > ${fmtLatencyMs(at.core_p95_ms)}（≥ ${at.min_samples} 次）`
        : rule.scope === 'other'
          ? `其他接口 · 近 ${window} 分钟 p95 > ${fmtLatencyMs(at.other_p95_ms)}（≥ ${at.min_samples} 次）`
          : `队列 · 近 ${at.queue_window_minutes} 分钟批处理 p95 > ${fmtLatencyMs(at.queue_p95_ms)}（≥ ${at.queue_min_samples} 批）`,
    note: rule.note,
  }));
  return [...logRules, ...paymentRules, ...apiRules];
}

// ---------- 接口时效卡 ----------

export interface ApiLatencyRow {
  key: string;
  kind: ServerMonitorApiLatencyItem['kind'];
  trigger: string;
  /** 接口：路径类别；队列：「队列」*/
  categoryLabel: string;
  /** 真实入库样本数（摄取采样已折算）*/
  count: number;
  /** 次数列展示：有摄取采样时标「采样」*/
  countLabel: string;
  p50: string;
  p95: string;
  p99: string;
  /** 超阈值才有级别（P0 / P1），其余 null */
  severity: 'P0' | 'P1' | null;
  /** insufficient → 「样本不足」；其余空串 */
  note: string;
  /** 悬停说明：适用阈值 */
  title: string;
}

export interface ApiLatencyView {
  tone: 'pos' | 'warn' | 'neg' | 'default';
  label: string;
  detail: string;
  /** 两类查询各自的窗口（接口与队列窗口不同）*/
  windowLabel: string;
  /** 截断 / 采样 / 查询失败的提示（逐条一行）*/
  warnings: string[];
  rows: ApiLatencyRow[];
  /** unconfigured / not_run / query_failed 时的整卡说明；有行时为 null */
  emptyNote: string | null;
}

const API_LATENCY_STATE: Record<
  ServerMonitorApiLatency['state'],
  { tone: ApiLatencyView['tone']; label: string }
> = {
  ok: { tone: 'pos', label: '正常' },
  partial: { tone: 'warn', label: '部分未知' },
  query_failed: { tone: 'neg', label: '查询失败' },
  unconfigured: { tone: 'default', label: '未配置' },
  not_run: { tone: 'warn', label: '尚无记录' },
};

function apiQueryWarning(label: string, q: ServerMonitorApiLatencyQuery | null): string | null {
  if (!q) return null;
  if (q.state === 'query_failed')
    return `${label}查询失败（${q.error_code ?? '未知原因'}），本轮不判`;
  if (q.state === 'sampled') return `${label}结果被 Cloudflare 采样，本轮不判（监控降级）`;
  if (q.degraded) return `${label}全部分组因摄取采样样本不足，本轮不判（监控降级）`;
  if (q.truncated) return `${label}分组数超过上限已截断：p95 较低的尾部未拿到，按未知处理`;
  return null;
}

function toApiLatencyRow(item: ServerMonitorApiLatencyItem, windowMinutes: number): ApiLatencyRow {
  const unit = item.kind === 'queue' ? '批' : '次';
  return {
    key: `${item.kind}|${item.trigger}`,
    kind: item.kind,
    trigger: item.trigger,
    categoryLabel:
      item.kind === 'queue' ? '队列' : SERVER_MONITOR_CATEGORY_META[item.category].label,
    count: item.samples,
    countLabel: item.sampled ? `${item.samples}（采样）` : String(item.samples),
    p50: fmtLatencyMs(item.p50_ms),
    p95: fmtLatencyMs(item.p95_ms),
    p99: fmtLatencyMs(item.p99_ms),
    severity: item.verdict === 'breach' ? item.severity : null,
    note: item.verdict === 'insufficient' ? '样本不足' : '',
    title: `阈值 p95 > ${fmtLatencyMs(item.threshold_ms)}（${item.severity}）· 近 ${windowMinutes} 分钟 ${item.samples} ${unit}${item.sampled ? `（摄取采样，估算 ${item.count}）` : ''}`,
  };
}

/** 「接口时效」卡；非本环境目标（api_latency=null）不展示 */
export function toApiLatencyView(
  overview: ServerMonitorOverview,
  nowMs: number,
): ApiLatencyView | null {
  const a = overview.api_latency;
  if (!a) return null;
  const meta = API_LATENCY_STATE[a.state];
  const detail =
    a.state === 'unconfigured'
      ? 'admin 缺 Telemetry token / account，接口时效不评估'
      : a.computed_at
        ? `${fmtMonitorTime(a.computed_at, nowMs)} · ${a.latency_ms ?? 0}ms`
        : 'cron 尚未写入接口时效结果';
  const rows = a.items.map((item) =>
    toApiLatencyRow(item, item.kind === 'queue' ? a.queue_window_minutes : a.window_minutes),
  );
  return {
    tone: meta.tone,
    label: meta.label,
    detail,
    windowLabel: `接口近 ${a.window_minutes} 分钟（≥ ${overview.rules.api_latency.thresholds.min_samples} 次）· 队列近 ${a.queue_window_minutes} 分钟（≥ ${overview.rules.api_latency.thresholds.queue_min_samples} 批）`,
    warnings: [apiQueryWarning('接口', a.routes), apiQueryWarning('队列', a.queues)].filter(
      (w): w is string => w !== null,
    ),
    rows,
    emptyNote:
      rows.length > 0
        ? null
        : a.state === 'unconfigured'
          ? 'admin Worker 缺少 Workers Observability Telemetry token 或 account，接口时效不评估。'
          : a.state === 'not_run'
            ? 'cron 尚未写入接口时效结果（每 2 分钟一轮）。'
            : a.state === 'query_failed'
              ? '最近一轮接口 / 队列查询都失败，见「告警」里的监控自身异常。'
              : `近 ${a.window_minutes} 分钟没有接口请求、近 ${a.queue_window_minutes} 分钟没有队列批处理记录。`,
  };
}

export interface PaymentLatencyView {
  tone: 'pos' | 'warn' | 'neg' | 'default';
  label: string;
  detail: string;
  /** 每个 provider 一行：`p95 · 积压 · 样本[ · 送达 p95]`，查询失败写错误码 */
  providers: Array<{ provider: ServerMonitorPaymentProvider; label: string; text: string }>;
}

const PAYMENT_LATENCY_STATE: Record<
  ServerMonitorPaymentLatency['state'],
  { tone: PaymentLatencyView['tone']; label: string }
> = {
  ok: { tone: 'pos', label: '正常' },
  partial: { tone: 'warn', label: '部分失败' },
  query_failed: { tone: 'neg', label: '查询失败' },
  unconfigured: { tone: 'default', label: '未配置' },
  not_run: { tone: 'warn', label: '尚无记录' },
};

/** 「通知与自检」卡的支付到账时效行；非本环境目标（payment_latency=null）不展示 */
export function toPaymentLatencyView(
  overview: ServerMonitorOverview,
  nowMs: number,
): PaymentLatencyView | null {
  const p = overview.payment_latency;
  if (!p) return null;
  const meta = PAYMENT_LATENCY_STATE[p.state];
  const detail =
    p.state === 'unconfigured'
      ? 'admin 缺 DATABASE_URL，时效规则不评估'
      : p.computed_at
        ? `${fmtMonitorTime(p.computed_at, nowMs)} · ${p.latency_ms ?? 0}ms · 近 ${p.window_minutes} 分钟`
        : 'cron 尚未写入时效结果';
  return {
    tone: meta.tone,
    label: meta.label,
    detail,
    providers: p.providers.map((row) => ({
      provider: row.provider,
      label: SERVER_MONITOR_PAYMENT_PROVIDER_LABELS[row.provider],
      text:
        row.state === 'query_failed'
          ? `查询失败 · ${row.error_code ?? '未知原因'}`
          : [
              `p95 ${fmtLatencyMs(row.processing_p95_ms)}`,
              row.backlog > 0 && row.oldest_backlog_received_at
                ? `积压 ${row.backlog}（最老 ${fmtMonitorTime(row.oldest_backlog_received_at, nowMs)}）`
                : `积压 ${row.backlog}`,
              `${row.samples}/${row.events} 笔已生效`,
              row.delivery_p95_ms !== null ? `送达 p95 ${fmtLatencyMs(row.delivery_p95_ms)}` : null,
              row.truncated ? '已截断' : null,
            ]
              .filter(Boolean)
              .join(' · '),
    })),
  };
}

// ---------- 评估状态 / 通知与自检 ----------

export interface EvalStatusView {
  tone: 'pos' | 'warn' | 'neg' | 'default';
  label: string;
  title: string;
}

export function toEvalStatus(overview: ServerMonitorOverview, nowMs: number): EvalStatusView {
  const tick = overview.notification?.last_tick;
  if (!overview.notification)
    return { tone: 'default', label: '告警在该环境后台查看', title: '本环境 cron 不评估该服务' };
  if (!tick) return { tone: 'warn', label: '尚无评估记录', title: 'cron 尚未写入评估结果' };
  const at = fmtMonitorTime(tick.at, nowMs);
  if (tick.query_failed)
    return {
      tone: 'neg',
      label: `评估失败 · ${at}`,
      title: `上一轮日志拉取失败（${tick.query_error_code ?? '未知原因'}）`,
    };
  if (nowMs - Date.parse(tick.at) > EVAL_STALE_MS)
    return { tone: 'warn', label: `评估停滞 · ${at}`, title: '超过 6 分钟没有新的评估' };
  return { tone: 'pos', label: `评估正常 · ${at}`, title: '上一轮 cron 评估成功' };
}

export interface NotificationView {
  groupLabel: string;
  feishuConfigured: boolean;
  lastPush: { ok: boolean; at: string } | null;
  lastPull: { tone: 'pos' | 'warn' | 'neg'; label: string; detail: string } | null;
}

export function feishuGroupName(service: ServerMonitorService): string {
  return SERVER_MONITOR_FEISHU_GROUP_NAMES[service];
}

export function toNotificationView(
  overview: ServerMonitorOverview,
  nowMs: number,
): NotificationView | null {
  const n = overview.notification;
  if (!n) return null;
  const tick = n.last_tick;
  return {
    groupLabel: `${n.feishu_group_name} · 签名校验`,
    feishuConfigured: n.feishu_configured,
    lastPush: n.last_notification
      ? { ok: n.last_notification.ok, at: fmtMonitorTime(n.last_notification.at, nowMs) }
      : null,
    lastPull: tick
      ? tick.query_failed
        ? {
            tone: 'neg',
            label: '失败',
            detail: `${fmtMonitorTime(tick.at, nowMs)} · ${tick.query_error_code ?? '未知原因'}`,
          }
        : {
            tone: tick.truncated || tick.sampled ? 'warn' : 'pos',
            label: tick.truncated ? '已截断' : tick.sampled ? '采样' : '正常',
            detail: `本轮 ${tick.raw_events} 条 · ${tick.latency_ms}ms`,
          }
      : null,
  };
}

// ---------- 错误日志 ----------

export interface LogRow {
  id: string;
  timeLabel: string;
  occurredAt: string;
  level: string;
  category: ServerMonitorCategory;
  categoryLabel: string;
  event: string;
  errorCode: string;
  httpStatus: number | null;
  route: string;
  requestId: string | null;
  requestIdShort: string;
  requestIdField: ServerMonitorLogItem['request_id_field'];
  cause: string | null;
  causeTruncated: boolean;
}

export function toLogRow(item: ServerMonitorLogItem, nowMs: number): LogRow {
  return {
    id: item.id,
    timeLabel: fmtLogTime(item.occurred_at, nowMs),
    occurredAt: item.occurred_at,
    level: item.level,
    category: item.category,
    categoryLabel: SERVER_MONITOR_CATEGORY_META[item.category].label,
    event: item.event,
    errorCode: item.error_code ?? '—',
    httpStatus: item.http_status,
    route: routeOf(item.method, item.path),
    requestId: item.request_id,
    requestIdShort: item.request_id
      ? item.request_id.length > 18
        ? `${item.request_id.slice(0, 18)}…`
        : item.request_id
      : '—',
    requestIdField: item.request_id_field,
    cause: item.cause,
    causeTruncated: item.cause_truncated,
  };
}

/** POSIX shell 单引号转义：值里的 `'` 写成 `'\''` */
function quoteShellArgument(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

/** 本地排查命令：按 requestId（无则按 event）在事件前后 5 分钟内查 */
export function buildLogQueryCommand(row: LogRow, service: ServerMonitorService): string {
  const t = Date.parse(row.occurredAt);
  const from = new Date(t - 5 * 60_000).toISOString();
  const to = new Date(t + 5 * 60_000).toISOString();
  // 单引号包住整段参数：`$metadata.requestId` 不加引号会被 shell 当变量展开
  const filter = row.requestId
    ? `--where ${quoteShellArgument(`${row.requestIdField}=${row.requestId}`)}`
    : `--event ${quoteShellArgument(row.event)}`;
  return `pnpm --filter @novel-admin/server logs --service ${service} ${filter} --from ${from} --to ${to}`;
}
